"use client";

import { motion, Transition } from "framer-motion";
import {
  useState,
  useEffect,
  useRef,
  Children,
  cloneElement,
  isValidElement,
  ReactNode,
} from "react";

interface AnimatedBackgroundProps {
  children: ReactNode;
  className?: string;
  defaultValue?: string;
  value?: string;
  transition?: Transition;
}

export function AnimatedBackground({
  children,
  className,
  defaultValue,
  value,
  transition,
}: AnimatedBackgroundProps) {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0, x: 0, y: 0 });

  const currentValue = value ?? internalValue;

  useEffect(() => {
    const updateDimensions = () => {
      if (!currentValue) {
        setDimensions({ width: 0, height: 0, x: 0, y: 0 });
        return;
      }

      if (containerRef.current && currentValue) {
        const target = containerRef.current.querySelector(`[data-id="${currentValue}"]`) as HTMLElement;
        if (target) {
          const { width, height, x, y } = target.getBoundingClientRect();
          const containerRect = containerRef.current.getBoundingClientRect();
          setDimensions({
            width,
            height,
            x: x - containerRect.x,
            y: y - containerRect.y,
          });
        }
      }
    }
    
    updateDimensions();

    const resizeObserver = new ResizeObserver(() => {
      updateDimensions();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
    };
  }, [currentValue, children]);

  return (
    <div
      ref={containerRef}
      className={`relative flex items-center ${className}`}
    >
      {dimensions.width > 0 && (
        <motion.div
          initial={false}
          animate={{
            width: dimensions.width,
            height: dimensions.height,
            x: dimensions.x,
            y: dimensions.y,
          }}
          transition={transition ?? { type: "spring", bounce: 0.2, duration: 0.6 }}
          className="absolute z-0 rounded-full bg-white"
        />
      )}
      {Children.map(children, (child) => {
        if (!isValidElement(child)) return child;

        const childValue = child.props["data-id"];
        const isActive = childValue === currentValue;

        const handleClick = (e: React.MouseEvent<HTMLElement>) => {
          if (child.props.onClick) {
            child.props.onClick(e);
          }
          if (value === undefined && childValue) { // Uncontrolled component
            setInternalValue(childValue);
          }
        };

        return cloneElement(child, {
          ...child.props,
          "data-active": isActive,
          onClick: handleClick,
        });
      })}
    </div>
  );
}
